import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { PublicOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import {
    resolveDimensionBaseline,
    resolvePlatformOverallAverage,
} from "../../utils/trustScoreCalculations";

const COMMUNITY_AVG_COLOR = "#34D399";

const COMPARISON_ROWS = [
    { key: "overall", label: "Overall" },
    { key: "sportsmanship", label: "Sportsmanship" },
    { key: "reliability", label: "Reliability" },
    { key: "teamPlay", label: "Team play" },
    { key: "integrity", label: "Integrity" },
    { key: "profileCompleteness", label: "Profile completeness" },
];

export function buildCommunityComparisonRows(dimensions, overallScore, baselines) {
    const overallAvg = resolvePlatformOverallAverage(baselines);
    const hasBaselines = !!baselines;

    return COMPARISON_ROWS.map(({ key, label }) => {
        const playerScore = key === "overall"
            ? overallScore
            : dimensions?.[key]?.score;
        const platformAvg = key === "overall"
            ? overallAvg
            : resolveDimensionBaseline(baselines, key, {
                fallback: hasBaselines ? 50 : null,
            });

        return {
            key,
            label,
            playerScore: Number.isFinite(playerScore) ? playerScore : null,
            platformAvg: Number.isFinite(platformAvg) ? platformAvg : null,
        };
    }).filter((row) => row.playerScore != null || row.platformAvg != null);
}

function ComparisonRow({ label, playerScore, platformAvg }) {
    const safePlayerScore = Number.isFinite(playerScore) ? Math.max(0, Math.min(100, playerScore)) : 0;
    const safePlatformAvg = Number.isFinite(platformAvg) ? Math.max(0, Math.min(100, platformAvg)) : null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.72)",
                        lineHeight: 1.2,
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        lineHeight: 1.2,
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0,
                    }}
                >
                    <Box component="span" sx={{ color: colors.primary.main }}>
                        {safePlayerScore}
                    </Box>
                    {safePlatformAvg != null ? (
                        <Box component="span" sx={{ color: "rgba(255,255,255,0.38)" }}>
                            {` / `}
                            <Box component="span" sx={{ color: COMMUNITY_AVG_COLOR }}>
                                {Math.round(safePlatformAvg)}
                            </Box>
                        </Box>
                    ) : null}
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "0.4rem",
                    borderRadius: "999px",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    overflow: "visible",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: `${safePlayerScore}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: `linear-gradient(90deg, ${colors.primary.main}, ${colors.accent.main})`,
                    }}
                />
                {safePlatformAvg != null ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: `${safePlatformAvg}%`,
                            transform: "translate(-50%, -50%)",
                            width: "3px",
                            height: "0.95rem",
                            borderRadius: "999px",
                            backgroundColor: COMMUNITY_AVG_COLOR,
                            boxShadow: `0 0 10px ${COMMUNITY_AVG_COLOR}88`,
                            zIndex: 2,
                        }}
                    />
                ) : null}
            </Box>
        </Box>
    );
}

ComparisonRow.propTypes = {
    label: PropTypes.string.isRequired,
    playerScore: PropTypes.number,
    platformAvg: PropTypes.number,
};

function LegendItem({ color, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Box
                sx={{
                    width: "0.55rem",
                    height: "0.55rem",
                    borderRadius: "50%",
                    backgroundColor: color,
                    boxShadow: `0 0 8px ${color}66`,
                }}
            />
            <Typography
                sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.48)",
                    lineHeight: 1,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

LegendItem.propTypes = {
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};

export default function ReputationVsCommunity({
    dimensions = null,
    overallScore = null,
    baselines = null,
}) {
    const rows = useMemo(
        () => buildCommunityComparisonRows(dimensions, overallScore, baselines),
        [dimensions, overallScore, baselines],
    );

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                height: "100%",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                <PublicOutlined sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.42)" }} />
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.text.primary,
                        lineHeight: 1.2,
                    }}
                >
                    Where you stand vs Community avg
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.95rem", flex: 1 }}>
                {rows.map((row) => (
                    <ComparisonRow
                        key={row.key}
                        label={row.label}
                        playerScore={row.playerScore}
                        platformAvg={row.platformAvg}
                    />
                ))}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", pt: "0.25rem" }}>
                <LegendItem color={colors.primary.main} label="You" />
                <LegendItem color={COMMUNITY_AVG_COLOR} label="Community avg" />
            </Box>
        </Box>
    );
}

ReputationVsCommunity.propTypes = {
    dimensions: PropTypes.object,
    overallScore: PropTypes.number,
    baselines: PropTypes.object,
};
